import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DoCheck,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { ActionMenuItem } from '@seahorse/ui';
import { MenuItem } from 'primeng/api';
import { Menu, MenuModule } from 'primeng/menu';
import { TieredMenu, TieredMenuModule } from 'primeng/tieredmenu';

/**
 * An `ActionMenuItem` (label, icon, danger, visible, disabled, command over a
 * context) that can also have nested `items`, which turns the menu into a
 * tiered one. A parent item needs no `command`.
 */
export interface ShMenuItem<T = unknown>
  extends Omit<ActionMenuItem<T>, 'command'> {
  command?: (context: T) => void;
  items?: ShMenuItem<T>[];
}

function resolve<T, C>(
  value: T | ((context: C) => T) | undefined,
  context: C,
  fallback: T
): T {
  if (value === undefined) return fallback;
  return typeof value === 'function'
    ? (value as (context: C) => T)(context)
    : value;
}

/** Two PrimeNG menu models are the same when everything PrimeNG renders is equal. */
function sameModel(a: MenuItem[], b: MenuItem[]): boolean {
  return (
    a.length === b.length &&
    a.every((item, i) => {
      const other = b[i];
      return (
        item.label === other.label &&
        item.icon === other.icon &&
        item.disabled === other.disabled &&
        item.styleClass === other.styleClass &&
        !item.command === !other.command &&
        sameModel(item.items ?? [], other.items ?? [])
      );
    })
  );
}

/**
 * A menu of actions, popup (default) or inline. Flat items render as a plain
 * menu, nested `items` as a tiered one.
 *
 *   <sh-menu #menu [items]="items" [context]="row" />
 *   <button (click)="menu.toggle($event)">...</button>
 *
 * `label` is a translation key (or a function returning one); `visible`,
 * `disabled` and `label` functions are evaluated against `context` when the
 * popup opens (inline: on every change detection, keeping the previous
 * PrimeNG model while nothing visible changed, so a page that builds `items`
 * in a getter does not re-render the menu endlessly). A command always runs
 * against the current `items` and `context`.
 */
@Component({
  selector: 'sh-menu',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [CommonModule, MenuModule, TieredMenuModule],
  template: `
    <p-tieredmenu
      *ngIf="nested(); else flat"
      [model]="model"
      [popup]="popup()"
      [appendTo]="overlayTarget()"
    ></p-tieredmenu>
    <ng-template #flat>
      <p-menu
        [model]="model"
        [popup]="popup()"
        [appendTo]="overlayTarget()"
      ></p-menu>
    </ng-template>
  `,
})
export class ShMenuComponent implements DoCheck {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly items = input.required<ShMenuItem<any>[]>();
  readonly context = input<unknown>();
  /** A popup menu opened with `toggle()` / `show()`; off renders the menu inline. */
  readonly popup = input(true);
  /** Where a popup is appended: the body by default. */
  readonly appendTo = input<'body' | 'self' | HTMLElement>('body');

  private readonly _translate = inject(TranslateService);
  private readonly _flat = viewChild(Menu);
  private readonly _tiered = viewChild(TieredMenu);

  protected readonly nested = computed(() =>
    this.items().some((item) => item.items?.length)
  );
  protected readonly overlayTarget = computed(() =>
    this.popup() && this.appendTo() !== 'self' ? this.appendTo() : undefined
  );

  /** What PrimeNG renders. */
  protected model: MenuItem[] = [];

  ngDoCheck(): void {
    if (!this.popup()) this._refresh();
  }

  toggle(event: Event): void {
    event.stopPropagation();
    this._refresh(true);
    (this._tiered() ?? this._flat())?.toggle(event);
  }

  show(event: Event): void {
    event.stopPropagation();
    this._refresh(true);
    (this._tiered() ?? this._flat())?.show(event);
  }

  hide(): void {
    (this._tiered() ?? this._flat())?.hide();
  }

  private _refresh(force = false): void {
    const next = this._build(this.items(), []);
    if (force || !sameModel(this.model, next)) {
      this.model = next;
    }
  }

  private _build(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    items: ShMenuItem<any>[],
    path: number[]
  ): MenuItem[] {
    const context = this.context();
    const result: MenuItem[] = [];
    items.forEach((item, index) => {
      if (!resolve(item.visible, context, true)) return;
      const itemPath = [...path, index];
      const children = item.items?.length
        ? this._build(item.items, itemPath)
        : undefined;
      // A parent whose children are all hidden has nothing to open.
      if (item.items?.length && !children?.length) return;
      result.push({
        label: this._translate.instant(resolve(item.label, context, '')),
        icon: item.icon,
        disabled: resolve(item.disabled, context, false),
        styleClass: item.danger ? 'p-menuitem-danger' : undefined,
        items: children,
        command: item.command ? () => this._run(itemPath) : undefined,
      });
    });
    return result;
  }

  private _run(path: number[]): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let list: ShMenuItem<any>[] | undefined = this.items();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let item: ShMenuItem<any> | undefined;
    for (const index of path) {
      item = list?.[index];
      list = item?.items;
    }
    item?.command?.(this.context());
  }
}
